/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState, useEffect, useRef } from 'react';

import { tracking, GoogleTagManager } from '@iwoca/frontend-tracking-library';
import { Icon } from '@iwoca/orion';
import classnames from 'classnames';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';

import styles from './BurgerMenu.module.css';
import { CloseSVG } from '../../../svgComponents';
import { getAmplitudeDefaultEventProperties } from '../../../tracking/amplitudeEvents';
import SaveApplicationLink from '../../SaveApplication/SaveApplicationLink';
import messages from '../Navigation.messages';

/**
 * defining an interface for component props
 */
interface BurgerMenuProps {
    intl: IntlShape;
    isMobile: boolean;
}

/**
 * a component to be used in both mobile and desktop versions
 */
const BurgerMenu: React.FC<BurgerMenuProps> = ({ intl, isMobile }) => {
    const [showMenu, setShowMenu] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    const onClickMenu = () => setShowMenu(!showMenu);

    // to track the clicked help link type
    const handleClickHelpLink = (ctaType: string) => {
        tracking.ampli.helpMethodSelected({
            CTA_type: ctaType,
            ...getAmplitudeDefaultEventProperties()
        });
    };

    //  to handle logout and track it
    const handleLogoutClick = () => {
        if (isMobile) {
            GoogleTagManager.trackClick({
                identifier: 'Logout',
                deviceType: 'Mobile'
            });
        } else {
            GoogleTagManager.trackClick({
                identifier: 'Logout',
                deviceType: 'Desktop'
            });
        }

        tracking.ampli.logoutClicked({
            ...getAmplitudeDefaultEventProperties()
        });

        window.location.href = '/my_account/logout/';
    };

    // used to detect clicks outside of the menu component
    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node)
            ) {
                setShowMenu(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    return (
        <div ref={menuRef}>
            <div className={styles.actionBtns}>
                <div
                    data-testid="burger-menu-btn"
                    className={styles.burgerMenuButton}
                    onClick={() => {
                        onClickMenu();
                        tracking.ampli.signupMenuOpened({});
                    }}>
                    {!isMobile && (
                        <FormattedMessage {...messages.menu} tagName="p" />
                    )}
                    {showMenu ? (
                        <div className={styles.closeMenu}>
                            <CloseSVG className="" />
                        </div>
                    ) : (
                        <Icon
                            icon="hamburgerMenu"
                            className={styles.burgerMenuButton}
                        />
                    )}
                </div>
            </div>
            {showMenu && (
                <>
                    {isMobile && (
                        <div
                            className={styles.mobileMenuBackground}
                            data-testid="mobile-menu-background"
                            onClick={onClickMenu}></div>
                    )}

                    <div
                        className={styles.burgerDropdown}
                        data-testid="burger-dropdown">
                        <ul>
                            {isMobile && (
                                <li>
                                    <SaveApplicationLink />
                                </li>
                            )}

                            <li>
                                <button
                                    onClick={() => {
                                        window.location.href = `tel:${intl.formatMessage(
                                            messages.callPhoneNumber
                                        )}`;
                                        handleClickHelpLink('phone');
                                    }}>
                                    <FormattedMessage {...messages.callUs} />
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => {
                                        handleClickHelpLink('chat');
                                        setShowMenu(!showMenu);
                                    }}
                                    className={classnames(
                                        'ChatSupportButton',
                                        styles.chatButton
                                    )}>
                                    <FormattedMessage {...messages.chatToUs} />
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => {
                                        window.location.href = `mailto:${intl.formatMessage(
                                            messages.email
                                        )}`;
                                        handleClickHelpLink('email');
                                    }}>
                                    <FormattedMessage {...messages.emailUs} />
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => {
                                        window.open('/faq/', '_blank');
                                        handleClickHelpLink('FAQ');
                                    }}>
                                    <FormattedMessage {...messages.faq} />
                                </button>
                            </li>

                            <li>
                                <button
                                    data-testid="logout"
                                    className={styles.logout}
                                    onClick={handleLogoutClick}>
                                    <FormattedMessage {...messages.logout} />
                                </button>
                            </li>
                        </ul>
                    </div>
                </>
            )}
        </div>
    );
};

export default injectIntl(BurgerMenu);
